import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchApi } from '../../services/api/search.api';
import SearchIcon from '../../components/icons/SearchIcon';
import { CourseOption } from '../../types/course.response';
import { getCourseKey } from '../../utils/courseUtils';

interface SearchbarProps {
  onAddCourse: (course: CourseOption) => void;
}

export default function Searchbar({ onAddCourse }: SearchbarProps) {
  const [query, setQuery] = useState('');
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hoveredCourse, setHoveredCourse] = useState<string | null>(null);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const getHoveredCourse = (): CourseOption | null => {
    if (!hoveredCourse) return null;
    return courses.find(course => getCourseKey(course) === hoveredCourse) || null;
  };

  const debouncedSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setCourses([]);
      setIsDropdownOpen(false);
      setHoveredCourse(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const results = await searchApi.searchCourses(searchQuery);
      setCourses(results);
      setIsDropdownOpen(true);

      if (results.length > 0) {
        setHoveredCourse(getCourseKey(results[0]));
      } else {
        setHoveredCourse(null);
      }
    } catch (err) {
      setError('Failed to search courses');
      console.error('Search error:', err);
      setHoveredCourse(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newQuery = e.target.value;
    setQuery(newQuery);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      debouncedSearch(newQuery);
    }, 200);
  };

  const handleCourseSelect = (courseOption: CourseOption) => {
    setQuery("");
    setIsDropdownOpen(false);
    setHoveredCourse(null);
    setCourses([]);

    onAddCourse(courseOption);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isDropdownOpen || courses.length === 0) return;

    switch (e.key) {
      case 'Enter': {
        e.preventDefault();
        const hoveredCourseObj = getHoveredCourse();
        if (hoveredCourseObj) {
          handleCourseSelect(hoveredCourseObj);
        }
        break;
      }
      case 'Escape':
        e.preventDefault();
        setIsDropdownOpen(false);
        setHoveredCourse(null);
        inputRef.current?.blur();
        break;
      case 'ArrowDown': {
        e.preventDefault();
        const currentIndex = hoveredCourse ? courses.findIndex(c => getCourseKey(c) === hoveredCourse) : -1;
        const nextIndex = currentIndex < courses.length - 1 ? currentIndex + 1 : 0;
        setHoveredCourse(getCourseKey(courses[nextIndex]));
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const currentUpIndex = hoveredCourse ? courses.findIndex(c => getCourseKey(c) === hoveredCourse) : 0;
        const prevIndex = currentUpIndex > 0 ? currentUpIndex - 1 : courses.length - 1;
        setHoveredCourse(getCourseKey(courses[prevIndex]));
        break;
      }
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setHoveredCourse(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div className="relative">
        <SearchIcon 
          width={16} 
          height={16} 
          strokeColor="#9ca3af" 
          strokeWidth={3}
          className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none z-10"
        />
        
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={'Search to save courses'}
          className="h-[34px] w-full bg-white pl-9 pr-3 py-2 relative outline-none box-border rounded-[6px] shadow-[0px_3px_3px_rgba(0,0,0,0.1)] border border-[#e0e0e0] transition-all duration-200 ease-in-out focus:border-blue-500 focus:shadow-md"
        />
        
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
          </div>
        )}
      </div>

      {isDropdownOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-y-auto z-50">
          {error ? (
            <div className="px-3 py-2 text-red-600 text-sm">
              {error}
            </div>
          ) : courses.length === 0 && !isLoading ? (
            <div className="px-3 py-2 text-gray-500 text-sm">
              No courses found
            </div>
          ) : (
            <>
              {courses.map((course) => {
                const courseKey = getCourseKey(course);
                const isHovered = courseKey === hoveredCourse;

                return (
                  <button
                    key={courseKey}
                    onClick={() => handleCourseSelect(course)}
                    onMouseEnter={() => setHoveredCourse(courseKey)}
                    onMouseLeave={() => setHoveredCourse(null)}
                    className={`w-full px-3 py-2 text-left border-b border-gray-100 focus:outline-none transition-colors duration-150 ${
                      isHovered
                        ? 'bg-blue-50 border-blue-200'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm text-gray-900 flex-shrink-0">
                          {course.courseInfo.subject} {course.courseInfo.number}
                        </span>
                        <span className="text-sm text-gray-600 truncate flex-1">
                          {course.courseInfo.titleShort}
                        </span>
                      </div>
                      {course.topic && (
                        <span className="text-xs text-gray-500 truncate">
                          {course.topic}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
